import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { VerificationCodeInputField } from 'components/Form';
import { Button } from 'components/Elements';
import Lottie from 'lottie-react';
import ShieldLottie from 'assets/lottie/shield.json';
import Cookies from 'js-cookie';

import { confirmIdentity } from '../api/confirmIdentity';
import { loginWithSMSOTP } from '../api/loginWithSMSOTP';
import { verifySMSOTP } from '../api/verifySMSOTP';
import { REMOTE_URLS } from 'config/remotes';
import { generateDeepLink, parseDeepLink } from 'react-deep-link';

const parsedDeepLinkData = parseDeepLink(
  new URLSearchParams(window.location.search)
);

export const ConfirmIdentity = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [useSMS, setUseSMS] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);

  const shieldControls = useAnimation();
  const contentControls = useAnimation();
  const hasSentSMS = useRef(false);

  useEffect(() => {
    Cookies.set('token', parsedDeepLinkData.state?.token, { expires: 1 / 144 }); // 10 minutes
    const sequenceAnimation = async () => {
      await shieldControls.start({
        x: 0,
        opacity: 1,
        transition: { duration: 0.6, ease: 'easeOut' },
      });
      await new Promise((resolve) => setTimeout(resolve, 300));
      await shieldControls.start({
        y: 0,
        transition: { type: 'spring', stiffness: 60, damping: 10 },
      });
      contentControls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
      });
    };
    sequenceAnimation();
  }, [shieldControls, contentControls]);

  const authorizeDomain = REMOTE_URLS[parsedDeepLinkData.state.app];

  const url = generateDeepLink({
    baseUrl: authorizeDomain,
    route: parsedDeepLinkData.state.redirectPath,
    state: {
      token: parsedDeepLinkData.state.token,
      identityVerified: 'true',
      ...parsedDeepLinkData.state,
    },
  });

  const handleVerify = async () => {
    setVerifying(true);
    setError('');

    try {
      if (useSMS) {
        const response = await verifySMSOTP({ code: verificationCode });
        if (response.user) {
          setSuccess(true);
          Cookies.set('identity_verified', 'true', { expires: 1 / 144 });
          window.location.href = url;
        } else {
          setError('Verification failed. Please try again.');
        }
      } else {
        const response = await confirmIdentity({
          authenticatorCode: verificationCode,
          token: parsedDeepLinkData.state.token,
        });

        if (response.verified === true) {
          setSuccess(true);
          Cookies.set('identity_verified', 'true', { expires: 1 / 144 });
          window.location.href = url;
        } else {
          setError('Verification failed. Please try again.');
        }
      }
    } catch (err) {
      console.log(err);
      setError('An error occurred during verification.');
    } finally {
      setVerifying(false);
    }
  };

  const handleUseSMS = async () => {
    if (hasSentSMS.current) return;
    hasSentSMS.current = true;
    setUseSMS(true);
    setSendingSMS(true);
    try {
      await loginWithSMSOTP();
      setSmsSent(true);
    } catch (err) {
      setError('Failed to send SMS. Please try again.');
      hasSentSMS.current = false;
    } finally {
      setSendingSMS(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-white"
    >
      <div className="flex flex-col items-center max-w-full w-full text-center">
        <motion.div
          initial={{ x: '-100vw', opacity: 0, y: -50 }}
          animate={shieldControls}
          className="mb-8 -mt-24"
        >
          <Lottie
            animationData={ShieldLottie}
            className="w-32 h-32 md:w-48 md:h-48"
            loop
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={contentControls}
          className="w-full bg-white px-4 sm:px-0"
        >
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            Wire24 Identity Verification
          </h2>
          <p className="text-gray-600 mb-2">
            To proceed with this action, verification through your configured
            two-factor authentication method is required.
          </p>

          {!useSMS ? (
            <>
              <p className="text-gray-600 mb-6">
                Please enter the 6-digit code sent via your authenticator app.
              </p>
              <p
                className="text-sm text-blue-600 mb-4 cursor-pointer hover:underline"
                onClick={handleUseSMS}
              >
                Can't access your authenticator app? <br />
                <strong>Use SMS instead</strong>
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6">
                {sendingSMS
                  ? 'Sending code to your mobile number...'
                  : 'A 6-digit code has been sent to your mobile number. Please enter it below to verify your identity.'}
              </p>
            </>
          )}

          {(useSMS && smsSent) || !useSMS ? (
            <div className="flex justify-center mt-4">
              <div>
                <VerificationCodeInputField
                  length={6}
                  onChange={(code) => setVerificationCode(code)}
                />
              </div>
            </div>
          ) : null}

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleVerify}
              isLoading={verifying}
              disabled={success || verificationCode.length !== 6}
            >
              {useSMS ? 'Verify SMS Code' : 'Authenticate & Proceed'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              disabled={verifying}
            >
              Cancel
            </Button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
